import React, { useEffect, useState } from "react";
import "./Contact.css";
import { useForm, ValidationError } from "@formspree/react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

const RECAPTCHA_ACTION = "contact_form_submit";
const RECAPTCHA_FALLBACK_DELAY_MS = 5000;

function Contact({ recaptchaEnabled = true }) {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [state, submitToFormspree, resetFormspreeState] = useForm("xkgzeqqw");
  const [localError, setLocalError] = useState("");
  const [isPreparingSubmission, setIsPreparingSubmission] = useState(false);
  const [allowRecaptchaFallback, setAllowRecaptchaFallback] = useState(false);

  const isRecaptchaReady =
    recaptchaEnabled && typeof executeRecaptcha === "function";
  const isWaitingForRecaptcha =
    recaptchaEnabled && !isRecaptchaReady && !allowRecaptchaFallback;
  const isSubmitting = state.submitting || isPreparingSubmission;
  const isSubmitDisabled = isSubmitting || isWaitingForRecaptcha;

  useEffect(() => {
    if (!recaptchaEnabled) {
      setAllowRecaptchaFallback(false);
      return undefined;
    }

    if (isRecaptchaReady) {
      setAllowRecaptchaFallback(false);
      return undefined;
    }

    const fallbackTimer = window.setTimeout(() => {
      setAllowRecaptchaFallback(true);
    }, RECAPTCHA_FALLBACK_DELAY_MS);

    return () => {
      window.clearTimeout(fallbackTimer);
    };
  }, [isRecaptchaReady, recaptchaEnabled]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    if (isSubmitting) {
      return;
    }

    if (isWaitingForRecaptcha) {
      setLocalError(
        "Contact verification is still loading. Please try again in a moment.",
      );
      return;
    }

    setLocalError("");
    setIsPreparingSubmission(true);

    try {
      const formData = new FormData(form);

      if (isRecaptchaReady) {
        const recaptchaToken = await executeRecaptcha(RECAPTCHA_ACTION);

        if (!recaptchaToken) {
          throw new Error("Missing reCAPTCHA token");
        }

        formData.set("g-recaptcha-response", recaptchaToken);
      }

      await submitToFormspree(formData);
    } catch {
      resetFormspreeState();
      setLocalError(
        "I couldn't send your message just now. Please try again in a moment.",
      );
    } finally {
      setIsPreparingSubmission(false);
    }
  };

  return (
    <section id="contact" className="section">
      <div className="container">
        <h2 className="section-title">Get In Touch</h2>

        {state.succeeded ? (
          <p className="form-success-message" role="status">
            Thank you for your message! I'll get back to you soon.
          </p>
        ) : (
          <form onSubmit={handleSubmit} className="contact-form">
            {isWaitingForRecaptcha && (
              <p className="form-status-message" role="status">
                Contact verification is loading. The form will be ready in a
                moment.
              </p>
            )}

            {localError && (
              <p className="form-error-message" role="alert">
                {localError}
              </p>
            )}

            <input
              id="name"
              type="text"
              name="name"
              placeholder="Your Name"
              required
              className="form-input"
            />
            <ValidationError
              prefix="Name"
              field="name"
              errors={state.errors}
              className="form-error-message"
              role="alert"
            />

            <input
              id="email"
              type="email"
              name="email"
              placeholder="Your Email"
              required
              className="form-input"
            />
            <ValidationError
              prefix="Email"
              field="email"
              errors={state.errors}
              className="form-error-message"
              role="alert"
            />

            <textarea
              id="message"
              name="message"
              placeholder="Your Message"
              rows="5"
              required
              className="form-input"
            ></textarea>
            <ValidationError
              prefix="Message"
              field="message"
              errors={state.errors}
              className="form-error-message"
              role="alert"
            />

            <ValidationError
              errors={state.errors}
              className="form-error-message"
              role="alert"
            />

            <button
              type="submit"
              disabled={isSubmitDisabled}
              className="form-button"
            >
              {isSubmitting ? "Sending..." : "Send Message"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}

export default Contact;
